import type { MobiLauncherConfig as Config } from '@schema/index.js';

/**
 * Browser-side API client for the licensing-server's admin endpoints.
 *
 * Auth model: the Builder is gated behind Cloudflare Access at the
 * domain level (build.mobilauncher.com). The admin bearer token is a
 * second hop — it identifies the request to the API even when Access
 * has authenticated the user. We store it in localStorage scoped to
 * the API base URL so a single browser can target multiple environments
 * (e.g., staging api vs production api).
 *
 * The token never leaves the browser except in the Authorization header
 * on requests to the configured API base URL — there's no telemetry,
 * no analytics, no leaking through query params.
 */

const TOKEN_KEY = 'mobilauncher.builder.adminToken.v1';

// The AdminDashboard is mounted on the PUBLIC customer portal (via ?admin=1),
// which — unlike the Builder at build.mobilauncher.com — is NOT behind
// Cloudflare Access. Its bearer is the god-mode ADMIN_BEARER_TOKEN (can revoke
// licenses, delete customers), so it must NOT live in localStorage where it
// persists indefinitely and any portal-origin XSS can exfiltrate it. We scope
// it to sessionStorage instead: cleared on tab close, not shared across tabs,
// and gone the moment the operator walks away. The Builder keeps localStorage
// because Access already fronts that origin.
const ADMIN_TOKEN_KEY = 'mobilauncher.portal.adminToken.session';

/**
 * Resolve the API base URL. Build-time env var takes precedence so the
 * Cloudflare Pages deploy can point at api.mobilauncher.com without
 * runtime config; falls back to localhost for dev when wrangler is
 * running the worker at the default port.
 */
export function getApiBaseUrl(): string {
  const fromEnv = import.meta.env['VITE_API_BASE_URL'] as string | undefined;
  if (fromEnv && fromEnv.length > 0) return fromEnv.replace(/\/+$/, '');
  // A deployed Builder with no API URL would silently call localhost; fail
  // loud in production instead. Dev keeps the localhost default.
  if (import.meta.env.PROD) {
    throw new Error(
      'VITE_API_BASE_URL is required for production builds — refusing to fall back to ' +
        'http://localhost:8787. Set it in the Cloudflare Pages project env before building.',
    );
  }
  return 'http://localhost:8787';
}

/** Read the stored admin bearer token, or null if the user hasn't entered one. */
export function getStoredToken(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

/** Persist the admin bearer token. Pass empty string to clear. */
export function setStoredToken(token: string): void {
  if (typeof window === 'undefined') return;
  try {
    if (token.length === 0) {
      window.localStorage.removeItem(TOKEN_KEY);
    } else {
      window.localStorage.setItem(TOKEN_KEY, token);
    }
  } catch {
    // Quota / private-mode failures — non-fatal, user just gets re-prompted next time.
  }
}

/**
 * Read the portal AdminDashboard bearer token from sessionStorage, or null.
 * Deliberately separate from {@link getStoredToken} (Builder/localStorage) —
 * see {@link ADMIN_TOKEN_KEY}.
 */
export function getAdminSessionToken(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage.getItem(ADMIN_TOKEN_KEY);
  } catch {
    return null;
  }
}

/** Persist the portal AdminDashboard bearer token for this tab session only. */
export function setAdminSessionToken(token: string): void {
  if (typeof window === 'undefined') return;
  try {
    if (token.length === 0) {
      window.sessionStorage.removeItem(ADMIN_TOKEN_KEY);
    } else {
      window.sessionStorage.setItem(ADMIN_TOKEN_KEY, token);
    }
  } catch {
    // Quota / private-mode failures — non-fatal, operator just re-enters it.
  }
}

/**
 * Outcome of a publish attempt. Discriminated union so the UI doesn't
 * have to introspect HTTP status codes — each variant carries exactly
 * the data the toolbar feedback needs to render its message.
 */
export type PublishResult =
  | { kind: 'ok'; etag: string; updatedAt: number }
  | { kind: 'unauthorized'; message: string }
  | { kind: 'invalidConfig'; issues: Array<{ path: (string | number)[]; message: string }> }
  | { kind: 'tenantMismatch'; message: string }
  | { kind: 'network'; message: string }
  | { kind: 'unexpected'; status: number; message: string };

/**
 * PUT the config to /admin/configs/:tenantId. The path's tenantId is
 * always taken from `config.tenantId` — the server enforces the same
 * (so a mismatch here is a clear bug, not a security check), so this
 * helper just reads it directly off the config and saves a round trip.
 */
export async function publishConfig(config: Config, token: string): Promise<PublishResult> {
  const tenantId = encodeURIComponent(config.tenantId);
  const url = `${getApiBaseUrl()}/admin/configs/${tenantId}`;

  let res: Response;
  try {
    res = await fetch(url, {
      method: 'PUT',
      headers: {
        'content-type': 'application/json',
        authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(config),
    });
  } catch (err) {
    return {
      kind: 'network',
      message: err instanceof Error ? err.message : 'network error',
    };
  }

  if (res.status === 401) {
    return { kind: 'unauthorized', message: 'Admin token rejected. Re-enter to retry.' };
  }
  if (res.status === 422) {
    const body = (await safeJson(res)) as { issues?: PublishResult extends { issues: infer I } ? I : never };
    return {
      kind: 'invalidConfig',
      issues: body.issues ?? [],
    };
  }
  if (res.status === 400) {
    const body = (await safeJson(res)) as { error?: string; details?: string };
    return {
      kind: 'tenantMismatch',
      message: body.details ?? body.error ?? 'bad request',
    };
  }
  if (res.status === 200) {
    const body = (await safeJson(res)) as { etag?: string; updatedAt?: number };
    if (typeof body.etag === 'string' && typeof body.updatedAt === 'number') {
      return { kind: 'ok', etag: body.etag, updatedAt: body.updatedAt };
    }
    return { kind: 'unexpected', status: 200, message: 'server returned 200 but malformed body' };
  }
  return {
    kind: 'unexpected',
    status: res.status,
    message: `unexpected status ${String(res.status)}`,
  };
}

async function safeJson(res: Response): Promise<unknown> {
  try {
    return await res.json();
  } catch {
    return {};
  }
}
